import CustomGeneratedIdSettings from "~/models/customGeneratedIdSettings";
import FormPaymentsDynamicFieldsHandlerMixin from "~/models/dynamicFields/handlers/formPaymentsDynamicFieldsHandler";
import FormSubmissionMetadataDynamicFieldsHandlerMixin from "~/models/dynamicFields/handlers/formSubmissionMetadataDynamicFieldsHandler";
import Form from "../../../../formability/form_builder/models/form";
import FormElement from "../../../../formability/form_builder/models/formElements/formElement";
import FormSection from "../../../../formability/form_builder/models/formSections/formSection";
import { Hash } from "../../../interfaces/hashInterfaces";
import FormElementOptions from "../options/formElementOptions";
import FormElementDynamicFieldsHandler from "./formElementDynamicFieldsHandler";

export default class FormFormulaBuilderDynamicFieldsHandler extends (FormSubmissionMetadataDynamicFieldsHandlerMixin(FormPaymentsDynamicFieldsHandlerMixin(FormElementDynamicFieldsHandler))) {
  SECTIONS = ['formFieldData'];
  dynamicMessage: string;
  showDynamicMessage: boolean;
  includeRepeatables: boolean;
  includePayment: boolean;

  elementOptions = new FormElementOptions({getElements: this.getElementsForFormula, matcher: null});

  constructor(options: Hash = {}) {
    super(options);
    if (options.elementOptions) {
      this.elementOptions = options.elementOptions;
    }

    // remove when back-end eval is implemented
    if (options.isFrontEndEval) {
      this.SECTIONS.push('submissionId');
    }

    if (options.includePayment) {
      this.SECTIONS.push('paymentInfo')
    }
  }

  getElementsForFormula(elementContainer: FormElement | FormSection, options: {includeRepeatables?, includePayment?} = {}): FormElement[] {
    return elementContainer.formElementsForFormula(options)
  }

  protected defaultFilter(parent: FormElement | Form | CustomGeneratedIdSettings | null): (el: FormElement) => boolean {
    /*
    `defaultFilter` creates a function that takes an `element` returns true if the element can be referenced from
    `parent`'s formula or false if it cannot be.

    `parent` is the form element that contains the formula, `element` is the form element that this function determines the eligibility of.
     */
    return element => {
      /*
      Only single values and arrays of single values are supported (nested arrays are not currently permitted).

      Therefore, `element` can be referenced if and only if the path through the element tree from `element` to
      `parent` goes down (descends into) no more than 1 repeatable context. The path is permitted to go up through any
      number of repeatable contexts, because from the perspective of a `parent` evaluating the formula, there will only
      be 1 value for `element` along the path from that specific instance of the `parent`.

      For example, given the elements A through G where A, B, and C are repeatable containers (non-repeatable containers
      are irrelevant for our purposes) arranged like so:

           A
          / \
         /   \
        B     C
       / \   / \
      D   E F   G

      Formulas added to each of these elements can reference the following elements (ignoring, for demonstration
      purposes, the fact that container elements like Grids and Stacks are not referencable):
      | Element | Referencable Elements |
      | ------- | --------------------- |
      | A       | A, B, C               |
      | B       | A, B, C, D, E         |
      | C       | A, B, C, F, G         |
      | D       | A, B, C, D, E         |
      | E       | A, B, C, D, E         |
      | F       | A, C, F, G            |
      | G       | A, C, F, G            |

      See features/formulas/forms/configure_and_use.feature for a test that covers this behavior.
       */

      /*
      Non-repeatable container elements are excluded from the repeatable contexts arrays because they are irrelevant for this determination.

      These arrays should be in order from outermost -> innermost elements. The .ancestors method returns the opposite order, so reverse it.

      The `element` and `parent` themselves are included in the context arrays because, for the purpose of this calculation,
      a repeatable element that has a value can be thought of as a repeatable context that contains a non-repeatable
      value (e.g., a repeatable text field is the same as a repeatable stack that contains only a non-repeatable text field).
       */
      const elementContexts = [...element.ancestors.reverse(), element].filter((e) => e.is_repeatable);
      // If `parent` is not a FormElement, it is not repeatable and cannot have any repeatable ancestors.
      const parentContexts = parent instanceof FormElement ? [...parent.ancestors.reverse(), parent].filter((e) => e.is_repeatable) : [];

      /*
      As we go through the elementContexts and parentContexts arrays, we expect each context to be the same. When we
      encounter one that differs, there are two possibilities:
      1. The differing context is the _last_ repeatable context for `element`. In this case, the resulting value from
         `parent`'s perspective would be a 1-dimensional array which is permitted.
      2. `element` has other repeatable contexts after the differing one. Because elements are organized in a tree,
         the proposed element coming _after_ a differing repeatable context means the resulting value will be a
         multi-dimensional array, which is not not allowed.
      In either case, we know and can return the result immediately upon encountering non-matching contexts.
       */
      for (let i = 0; i < elementContexts.length; i++) {
        if (elementContexts[i].client_id !== parentContexts[i]?.client_id) {
          return i === elementContexts.length - 1;
        }
      }

      /*
      If we make it here, then either elementContexts is a prefix of parentContexts (meaning the path goes directly
      upwards) or elementContexts is the same as parentContexts (meaning `element` and `parent` are siblings).
      Both of these results mean the element is acceptable.
       */
      return true;
    }
  }
}